export type ServiceType =
  | "port"
  | "internet"
  | "dci"
  | "cloud"
  | "fwaas"
  | "sdwan"
  | "carrier_access";
export type LabelValue = { label: string; value: string };

export type TenantInfo = { id: number; name: string; nameDisplay?: string };
export type ServiceItem = {
  id: number;
  name?: string;
  subscriptionId?: string | null;
  type: ServiceType;
  customer?: TenantInfo;
};

export const stripNonAlphanumeric = (s: string) => s.replace(/[\s\W_]+/g, "");
export const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// Generic mapper for backend arrays -> ServiceItem[]
export const mapToServiceItems = <T extends {
  id: number; name?: string; subscriptionId?: string; customer?: TenantInfo;
}>(
  items: T[] | undefined,
  fallbackPrefix: string,
  type: ServiceType
): ServiceItem[] => {
  const arr = Array.isArray(items) ? items : [];
  return arr.map((i) => ({
    id: i.id,
    name: i.name ?? `${fallbackPrefix}-${i.id}`,
    subscriptionId: i.subscriptionId ?? null,
    type,
    customer: i.customer,
  }));
};

/**
 * Carrier Access rows as ServiceItems. The `84-` id is part of the label
 * (Jose, PR #696): it is the id support asks for, and it tells two accesses
 * with the same name apart — selection is resolved by label.
 */
export const mapCarrierAccessToServiceItems = (
  items:
    | {
        id: number;
        name?: string | null;
        subscriptionId?: string;
        customer?: TenantInfo;
      }[]
    | undefined
): ServiceItem[] =>
  mapToServiceItems(
    (Array.isArray(items) ? items : []).map((i) => ({
      ...i,
      name: i.name ? `${i.name} (${i.subscriptionId})` : i.subscriptionId,
    })),
    "Carrier Access",
    "carrier_access"
  );

// Priority helpers
export const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"] as const;
export type PriorityLabel = typeof PRIORITY_OPTIONS[number];

/** Change Request mapping (KE: Urgent=10000, else use 4/3/2) */
export const mapCRPriority = (p: PriorityLabel, isKontrolEdge: boolean) => {
  if (p === "Urgent") return isKontrolEdge ? 10000 : 1; //Validate
  if (p === "High")   return 2;
  if (p === "Medium") return 3;
  return 4; // Low
};

/** Incident Report mapping **/
export const mapIRPriority = (val: string) => parseInt(val, 10);

// Reusable validations
export const isGeneralInfoValidCR = (isKE: boolean, topic: string, twoLevel: string) =>
  isKE ? true : topic.trim() !== "" && twoLevel.trim() !== "";

export const isGeneralInfoValidIR = (isKE: boolean, topic: string, affectedServices: string) =>
  isKE ? true : topic.trim() !== "" && affectedServices.trim() !== "";

/**
 * A Kontrol Edge ticket has to name the service(s) it is about — and, in
 * partner view, the end customer too. Support cannot route a ticket or cross
 * it against the inventory without them (CTX-533), so this gates the Next
 * button of the service selection step on every KE ticket modal.
 */
export const isServiceSelectionValid = (
  isPartnerView: boolean,
  selectedCustomerId: number | null,
  selectedServiceIds: number[]
): boolean => {
  if (isPartnerView && !selectedCustomerId) return false;
  return selectedServiceIds.length > 0;
};

export const isAdditionalInfoValid = (
  isKE: boolean,
  { cloud, region, title, description, priority }: { cloud: string; region: string; title: string; description: string; priority: string; }
) => {
  // For Kontrol Edge: validate priority, title, and description
  if (isKE) {
    const titleClean = stripNonAlphanumeric(title);
    const descClean = stripNonAlphanumeric(description);
    return (
      priority.trim() !== "" &&
      titleClean.length >= 5 &&
      descClean.length >= 10
    );
  }
  const titleClean = stripNonAlphanumeric(title);
  const descClean = stripNonAlphanumeric(description);
  return (
    cloud.trim() !== "" &&
    region.trim() !== "" &&
    titleClean.length >= 5 &&
    descClean.length >= 10 &&
    priority.trim() !== ""
  );
};

export const isAttachmentsValid = (
  isKE: boolean,
  sendResponse: boolean,
  contactName: string,
  contactEmail: string
) => {
  if (isKE || !sendResponse) return true;
  return contactName.trim().length >= 3 && isValidEmail(contactEmail.trim());
};

// Service Type Display Mapping
export const SERVICE_TYPE_DISPLAY_MAP: Record<ServiceType, string> = {
  port: "Port",
  internet: "Internet",
  dci: "Datacenter",
  cloud: "Cloud",
  fwaas: "FWaaS",
  sdwan: "SDWAN",
  carrier_access: "Carrier Access",
};

/**
 * Maps a service type to its display name
 * @param type - Service type (case-insensitive)
 * @returns Display name for the service type
 */
export const getServiceTypeDisplay = (type: ServiceType | string): string => {
  return SERVICE_TYPE_DISPLAY_MAP[type.toLowerCase() as ServiceType] || type;
};

/**
 * Formats a service item label for dropdown display: [ServiceType] • name
 * @param item - Service item to format
 * @returns Formatted label string
 */
export const getServiceDropdownLabel = (item: ServiceItem): string => {
  const serviceType = getServiceTypeDisplay(item.type);
  const serviceName = item.name ?? `${item.type.toUpperCase()}-${item.id}`;
  return `[${serviceType}] • ${serviceName}`;
};


// Customer Info Type
export type CustomerInfo = { id: number; nameDisplay: string };
/**
 * Extracts unique customers from an array of service items
 * @param serviceItems - Array of service items
 * @returns Array of unique customer information
 */
export const extractUniqueCustomers = (serviceItems: ServiceItem[]): CustomerInfo[] => {
  const customerMap = new Map<number, CustomerInfo>();
  serviceItems.forEach((item) => {
    if (item.customer) {
      const customerId = item.customer.id;
      const nameDisplay = item.customer.nameDisplay ?? item.customer.name ?? "";
      if (!customerMap.has(customerId)) {
        customerMap.set(customerId, { id: customerId, nameDisplay });
      }
    }
  });
  return Array.from(customerMap.values());
};

/**
 * Filters service items by customer ID
 * @param serviceItems - Array of service items to filter
 * @param customerId - Customer ID to filter by (null returns empty array)
 * @returns Filtered array of service items
 */
export const filterServicesByCustomer = (
  serviceItems: ServiceItem[],
  customerId: number | null
): ServiceItem[] => {
  if (!customerId) return [];
  return serviceItems.filter((item) => item.customer?.id === customerId);
};

// Finder helpers for LabelValue arrays
/**
 * Finds a product by label in productData array
 * @param productData - Array of product LabelValue items
 * @param label - Label to search for
 * @returns Found product or undefined
 */
export const findProductByLabel = (productData: LabelValue[], label: string): LabelValue | undefined =>
  productData.find((p) => p.label === label);

/**
 * Finds a topic by label in topics array
 * @param topics - Array of topic LabelValue items
 * @param label - Label to search for
 * @returns Found topic or undefined
 */
export const findTopicByLabel = (topics: LabelValue[], label: string): LabelValue | undefined =>
  topics.find((t) => t.label === label);

/**
 * Finds a two-level option by label in twoLevels array
 * @param twoLevels - Array of two-level LabelValue items
 * @param label - Label to search for
 * @returns Found two-level option or undefined
 */
export const findTwoLevelByLabel = (twoLevels: LabelValue[], label: string): LabelValue | undefined =>
  twoLevels.find((t) => t.label === label);
